/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/emailSettings.scss';

import {ChangeFunction, Link, Loader, WithBackground} from '@dvrd/dvr-controls';
import React, {MouseEventHandler, useState} from 'react';
import BolmateSwitch from "../../controls/switch/bolmateSwitch";
import {BolmateButton, BolmateNumberInput} from "../../controls";
import InfoIcon from "../../icon/infoIcon";
import useRight from "../../misc/useRight";
import {AccessKey} from "../../../util/interfaces";

interface Props {
    onChangeMaxProductLength: ChangeFunction<string>;
    onChangeNoReplyTracking: ChangeFunction<boolean>;
    onChangeIncludeUBLAttachment: ChangeFunction<boolean>;
    onSubmit: MouseEventHandler;
    noReplyTracking: boolean;
    onChangeSendToCanceled: ChangeFunction<boolean>;
    maxProductLength: string;
    loading: boolean;
    sendToCanceled: boolean;
    includeUBLAttachment: boolean;
    changed: boolean;
}

export default function EmailSettings(props: Props) {
    const [canEdit] = useRight(AccessKey.EMAIL_SETTINGS_EDIT);
    const {
        onChangeNoReplyTracking, noReplyTracking, onChangeMaxProductLength, maxProductLength, loading, onSubmit,
        sendToCanceled, onChangeSendToCanceled, onChangeIncludeUBLAttachment, includeUBLAttachment, changed
    } = props;
    const [noReplyInfoActive, setNoReplyInfoActive] = useState(false);

    function onOpenNoReplyInfo() {
        setNoReplyInfoActive(true);
    }

    function onCloseNoReplyInfo() {
        setNoReplyInfoActive(false);
    }

    function renderNoReplyInfo() {
        return (
            <WithBackground active={noReplyInfoActive} onClose={onCloseNoReplyInfo}>
                <div className='tracking-decision-info-popup default-popup'>
                    <div className='body-container'>
                        <p>In april 2022 heeft Bol een update in zijn e-mailsysteem doorgevoerd waardoor externe
                            links
                            (alle links die niet direct naar Bol leiden) als tekst worden toegevoegd in de mail.
                            Bol geeft aan dit uit veiligheidsoverwegingen te hebben gedaan, omdat klanten hierdoor
                            kunnen zien waar ze heen gestuurd worden voordat ze op een link / knop klikken.<br/><br/>Door
                            deze update werd de layout van de e-mails die via ons platform werden verzonden mogelijk
                            verstoord, hierdoor hebben wij onze statistieken van de e-mails tijdelijk
                            uitgeschakeld.<br/><br/>Inmiddels hebben we in gesprek met Bol een oplossing gevonden
                            waarmee wij weer statistieken van de e-mails kunnen tonen. Met deze oplossing worden de
                            e-mails verzonden door een "noreply" adres, waardoor het platform van Bol de mail anders
                            verwerkt en niet de links als tekst in de mails plaatst. De klant zal de mail ontvangen van
                            het adres noreply@verkopen.Bol. Het gevolg hiervan is dat de klant niet meer zal kunnen
                            reageren op de e-mail, omdat deze van het "noreply" adres van Bol komt.</p>
                        <p><br/><b>Let op:</b> bij het gebruik van bepaalde kernwoorden in het onderwerp van de mail zal
                            de e-mail ongeacht je keuze door het "noreply" adres van Bol verzonden worden. Bol
                            geeft aan dat het om de volgende kernwoorden gaat:<br/><i>onderweg, verstuurd, verzonden,
                                shipped, is geleverd, factuur, bevestiging, track & trace, track and trace, nieuwsbrief,
                                wil je ons helpen, bedankt, thank, dankjewel, dankuwel, pakbon, wat vindt u van,
                                levering, ingepakt, bezorgd, ingepland, aangemeld, afgeleverd, verzending, ontvangen,
                                nota, invoice, pending, planned, received</i></p>
                    </div>
                    <div className='footer-container'>
                        <BolmateButton onClick={onCloseNoReplyInfo} label='Sluiten' secondary/>
                    </div>
                </div>
            </WithBackground>
        )
    }

    function renderProductLength() {
        let productName = 'Le Creuset Signature Braadpan - 4,2 l - 24 cm - Kersenrood';
        if (maxProductLength) {
            const maxLength = Number(maxProductLength);
            if (maxLength < productName.length) productName = productName.substring(0, maxLength) + '...';
        }
        return (
            <div className='product-length-container'>
                <BolmateNumberInput onChange={onChangeMaxProductLength} value={maxProductLength}
                                    label='Maximaal aantal tekens in productnaam'
                                    inputProps={{placeholder: 'Onbeperkt'}} disabled={!canEdit} wholeNumbers
                                    disableSymbols/>
                <div className='info-container'>
                    <InfoIcon/>
                    <p className='info-text'>
                        Met deze instelling kan je instellen hoeveel tekens er maximaal in de e-mails gebruikt
                        worden voor de productnaam. Producten met een langere naam dan dit aantal tekens worden
                        afgekapt door "...".
                    </p>
                </div>
                <label className='example-product'>Voorbeeld: <i>{productName}</i></label>
            </div>
        );
    }

    function renderSendToCanceled() {
        const label = sendToCanceled ? 'WEL' : 'NIET';
        return (
            <div className='settings-part'>
                <div className='settings-header with-info'>
                    <label className='settings-label'>Mails voor geannuleerde bestellingen verzenden</label>
                    <label className='settings-sub-label'>Met deze instelling bepaal je of er mails verzonden moeten
                        worden voor bestellingen die geannuleerd zijn.</label>

                </div>
                <BolmateSwitch value={sendToCanceled} onChange={onChangeSendToCanceled}
                               labels={['', `E-mails voor geannuleerde bestellingen worden ${label} verzonden`]}
                               disabled={!canEdit}/>
            </div>
        )
    }

    function renderNoReply() {
        const label = noReplyTracking ? 'WEL' : 'NIET';
        const statsLabel = noReplyTracking ? 'WEL' : 'GEEN';
        const replyLabel = noReplyTracking ? 'NIET' : 'WEL';
        return (
            <div className='settings-part'>
                <div className='settings-header with-info'>
                    <label className='settings-label'>E-mail statistieken <Link
                        onClick={onOpenNoReplyInfo} className='info-link'><InfoIcon
                        className='info-icon'/>&nbsp;Meer informatie</Link></label>
                    <label className='settings-sub-label'>Gebruik de "noreply" afzender om statistieken over je
                        mails te kunnen verzamelen.</label>

                </div>
                <BolmateSwitch value={noReplyTracking} onChange={onChangeNoReplyTracking}
                               labels={['', `"noreply" afzender wordt ${label} gebruikt, er worden ${statsLabel} 
                               mail statistieken verzameld, klanten kunnen ${replyLabel} rechtstreeks reageren op de 
                               mails`]} disabled={!canEdit}/>
            </div>
        )
    }

    function renderUBLAttachment() {
        const label = includeUBLAttachment ? 'WEL' : 'NIET';
        return (
            <div className='settings-part'>
                <div className='settings-header'>
                    <label className='settings-label'>UBL bijlage</label>
                    <label className='settings-sub-label'>Voeg naast de PDF factuur ook een UBL bestand toe aan de
                        e-mails. UBL bestanden worden vaak gebruikt voor automatische verwerking in de
                        boekhouding.</label>

                </div>
                <BolmateSwitch value={includeUBLAttachment} onChange={onChangeIncludeUBLAttachment}
                               labels={['', `UBL bijlages ${label} toevoegen aan de e-mails.`]} disabled={!canEdit}/>
            </div>
        )
    }

    return (
        <>
            <div className='email-settings'>
                <Loader active={loading}/>
                {renderNoReply()}
                {renderUBLAttachment()}
                {renderSendToCanceled()}
                <div className='settings-part'>
                    <div className='settings-header'>
                        <label className='settings-label'>Productnaam</label>
                        <label className='settings-sub-label'>Beperk het aantal tekens dat gebruikt wordt voor de
                            productnaam in e-mails.</label>
                    </div>
                    {renderProductLength()}
                </div>
                <BolmateButton onClick={onSubmit} label='Opslaan' disabled={loading || !canEdit || !changed}/>
            </div>
            {renderNoReplyInfo()}
        </>
    )
}